import {
	SettlementBonusRowActions,
	SettlementBonusStatus,
} from '@/settlement/settlement-bonus-list-v2/_models/settlement-bonus-list-v2.model';

export const TECHNICZNY_FLAT_AMOUNT = 36000;
export const DOJAZD_FLAT_AMOUNT = 15000;
export const PRACA_FALLBACK_RATE = 40000;

export interface DriverTourismEvent {
	id: string;
	newId: number | null;
	dateFrom: string;
	dateTo: string;
	category: string;
	status: string;
	name: string;
	vehicleLabel: string | null;
	excludedFromSettlement: boolean;
	settlementExclusionComment: string | null;
	nawigatorEventId: number | null;
}

export interface EventExclusionPayload {
	month: string;
	excluded: boolean;
	comment: string | null;
}

export interface EventExclusionResponse {
	eventId: string;
	month: string;
	excludedFromSettlement: boolean;
	settlementExclusionComment: string | null;
	updatedAt: string | null;
	updatedBy: string | null;
}

export interface NawigatorPullStats {
	created: number;
	updated: number;
	removed: number;
	adopted: number;
	skipped_duplicate: number;
	skipped_no_driver: Array<{ nawigator_id: number; name: string }>;
	errors: number;
}

export interface NavbridgeBonusesStats {
	created: number;
	updated: number;
	skipped_duplicate: number;
	skipped_locked: number;
	skipped_no_driver: number;
	skipped_other_driver: number;
	errors: number;
	drafts_created?: number;
	drafts_removed?: number;
}

export interface DriverWorkRate {
	id: number;
	driver: number;
	dateFrom: string;
	dateTo: string | null;
	workRate: number | string;
	breakRate: number | string | null;
	otherRate: number | string | null;
	periodType: number;
	monthlyAmount: number | null;
}

export const PERIOD_TYPE_RATE = 1;
export const PERIOD_TYPE_MONTHLY = 2;

export interface EventMonthSpan {
	total: number;
	index: number;
}

export interface PracaEventRow {
	id: string;
	dateFrom: string;
	dateTo: string;
	daysInMonth: number;
	amount: number;
	excluded: boolean;
	comment: string | null;
	monthSpan: EventMonthSpan;
	nawigatorEventId: number | null;
}

export interface TechnicznyEventRow {
	id: string;
	dateFrom: string;
	dateTo: string;
	amount: number;
	excluded: boolean;
	comment: string | null;
	monthSpan: EventMonthSpan;
	nawigatorEventId: number | null;
}

export interface DojazdEventRow {
	id: string;
	dateFrom: string;
	dateTo: string;
	amount: number;
	excluded: boolean;
	comment: string | null;
	monthSpan: EventMonthSpan;
	nawigatorEventId: number | null;
}

export type GratyfikantSkippedReason = 'no_db' | 'fetch_failed';

export interface GratyfikantSkippedOrganization {
	organization_id: number;
	organization_name: string;
	reason: GratyfikantSkippedReason;
	error?: string;
}

export interface GratyfikantSalariesResponse {
	data: Record<string, unknown>[];
	meta: {
		skipped: GratyfikantSkippedOrganization[];
		fetched_count: number;
	};
}

export interface SalaryCacheResponse {
	id?: number | null;
	gratyfikant_netto?: number | null;
	gratyfikant_currency?: string | null;
	enova_netto?: number | null;
	enova_currency?: string | null;
	settlement_notes?: string | null;
	payment_notes?: string | null;
	settled_at?: string | null;
	settled_by?: string | null;
	cash_payout_pln?: number | null;
	cash_payout_eur?: number | null;
	cash_payout_date?: string | null;
	/** Who counted the money out - set by whoever last changed the amount, here or at the counter. */
	cash_payout_by?: string | null;
	kasa_settle_pln?: number | null;
	kasa_settle_eur?: number | null;
	kasa_settle_eur_pln?: number | null;
	kasa_carried_pln?: number | null;
	kasa_carried_eur?: number | null;
	kasa_eur_in_cash?: boolean | null;
}

export interface SalaryKasaSettlementPayload {
	kasa_settle_pln?: number | null;
	kasa_settle_eur?: number | null;
	kasa_eur_in_cash?: boolean;
}

export interface DriverSettlementBonus {
	id: string;
	ownerId: string;
	name: string;
	settlementBonusNameId: string;
	value: string;
	valueGrosze: number;
	currency: string | null;
	valuePln: string | null;
	valuePlnGrosze: number | null;
	exchangeRate: string | null;
	exchangeRateDate: string | null;
	plnConversionMissing: boolean;
	date: string;
	settlementDayFrom: string;
	settlementDayTo: string;
	termsInMonth: number;
	bonusDate: string;
	bonusValueGrosze: number;
	comment: string | null;
	isSettled: string;
	confirmAt: string | null;
	rejectedAt: string | null;
	rejectReason: string | null;
	status: SettlementBonusStatus;
	actions: SettlementBonusRowActions;
}

export type SalaryDraftSource = 'gratyfikant' | 'enova' | 'nawigator_event' | 'navbridge_bonus';

export type SalaryDraftStatus = 'pending' | 'rejected' | 'applied';

export interface SalaryDraftNettoPayload {
	current_netto: number | null;
	proposed_netto: number;
	currency: string;
}

export type SalaryDraftEventChangeType = 'new' | 'changed' | 'removed';

export interface SalaryDraftEventPayload {
	dedup_key: string;
	symbol: string;
	date_from: string;
	date_to: string;
	person: string;
	change_type?: SalaryDraftEventChangeType;
	current_date_from?: string;
	current_date_to?: string;
	current_symbol?: string;
}

export interface SalaryDraftBonusPayload {
	name: string;
	value: string;
	date: string;
	comment: string;
	external_key: string;
	bonus_id?: number;
	current_value?: string;
	current_date?: string;
}

export interface SalaryDraft {
	id: number;
	source: SalaryDraftSource;
	status: SalaryDraftStatus;
	payload: SalaryDraftNettoPayload | SalaryDraftEventPayload | SalaryDraftBonusPayload;
	created_at: string | null;
	rejected_at: string | null;
	rejected_by: string | null;
	applied_at: string | null;
	applied_by: string | null;
}

export interface SalaryDraftsResponse {
	data: SalaryDraft[];
}

export interface SalaryDraftNettoRow {
	id: number;
	label: string;
	currentNetto: number | null;
	proposedNetto: number;
	diff: number | null;
}

export interface SalaryDraftEventRow {
	id: number;
	symbol: string;
	dateFrom: string;
	dateTo: string;
	person: string;
	changeType: SalaryDraftEventChangeType;
	currentDateFrom: string | null;
	currentDateTo: string | null;
	currentSymbol: string | null;
}

export interface SalaryDraftBonusRow {
	id: number;
	name: string;
	valueGrosze: number;
	date: string;
	currentValueGrosze: number | null;
	currentDate: string | null;
}

export interface SalaryNotesPayload {
	settlement_notes: string;
	payment_notes: string;
}

/** Only the keys sent are touched, so the amounts and the date are saved independently. */
export interface SalaryCashPayoutPayload {
	cash_payout_pln?: number | null;
	cash_payout_eur?: number | null;
	cash_payout_date?: string | null;
}
